import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";

// Pages
import LoginPage from "./pages/LoginPage";
import SSOCallbackPage from "./pages/SSOCallbackPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import DashboardPage from "./pages/DashboardPage";
import BudgetPage from "./pages/BudgetPage";
import BudgetsPage from "./pages/BudgetsPage";
import UploadPage from "./pages/UploadPage";
import FilesPage from "./pages/FilesPage";
import FileDetailPage from "./pages/FileDetailPage";
import ImportExportPage from "./pages/ImportExportPage";
import InsightsPage from "./pages/InsightsPage";
import ReportsPage from "./pages/ReportsPage";
import WhatIfPage from "./pages/WhatIfPage";
import FinancialReviewPage from "./pages/FinancialReviewPage";
import SettingsPage from "./pages/SettingsPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ForecastsPage from "./pages/ForecastsPage";
import ForecastDetailPage from "./pages/ForecastDetailPage";
import CashflowPage from "./pages/CashflowPage";

// Protected Route wrapper
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Force password change if required
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  
  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route wrapper (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }
  
  if (user && !user.must_change_password) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Password Change Route - requires auth but not layout
const PasswordChangeRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/reset-password" 
        element={<LoginPage />} 
      />
      
      {/* SSO Callback - Public route for handling Microsoft redirect */}
      <Route 
        path="/auth/sso/callback" 
        element={<SSOCallbackPage />} 
      />
      
      {/* Password Change - No layout, but requires auth */}
      <Route 
        path="/change-password" 
        element={
          <PasswordChangeRoute>
            <ChangePasswordPage />
          </PasswordChangeRoute>
        } 
      />
      
      {/* Protected Routes */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/budget" 
        element={
          <ProtectedRoute>
            <BudgetPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/budgets" 
        element={
          <ProtectedRoute>
            <BudgetsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/financial-review" 
        element={
          <ProtectedRoute>
            <FinancialReviewPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/approvals" 
        element={
          <ProtectedRoute>
            <ApprovalsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forecasts" 
        element={
          <ProtectedRoute>
            <ForecastsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/forecasts/:forecastId" 
        element={
          <ProtectedRoute>
            <ForecastDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/cashflow" 
        element={
          <ProtectedRoute>
            <CashflowPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/what-if" 
        element={
          <ProtectedRoute>
            <WhatIfPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <UploadPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/import-export" 
        element={
          <ProtectedRoute>
            <ImportExportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/files" 
        element={
          <ProtectedRoute>
            <ImportExportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/files/:id" 
        element={
          <ProtectedRoute>
            <FileDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/insights" 
        element={
          <ProtectedRoute>
            <InsightsPage />
          </ProtectedRoute>
        } 
      />
      {/* Settings - Admin only */}
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute requireAdmin>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
